var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// x-plain-input.ts
import { isIos } from "@adornis/base/client/frontend-helpers";
import "@adornis/base/utilTypes";
import { injectAttributesOf } from "@adornis/chemistry/directives/inject-attributes";
import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { live } from "lit/directives/live.js";
import { ref } from "lit/directives/ref.js";
import { DateTime } from "luxon";
import { fromEvent } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { FormField } from "./form-field";
var inputResetStyle = {
  ":host": { display: "block", height: "1em" },
  input: {
    height: "100%",
    outline: "none",
    width: "100%",
    background: "var(--background, transparent)",
    textAlign: "inherit",
    border: "none",
    margin: "0",
    padding: "0",
    color: "inherit",
    fontWeight: "inherit",
    fontSize: "inherit",
    fontFamily: "inherit",
    "&::placeholder": {
      color: "var(--placeholder-color, darkgrey)"
    },
    // remove number spinner
    "&[type=number]::-webkit-outer-spin-button": {
      WebkitAppearance: "none",
      margin: "0"
    },
    "&[type=number]": { MozAppearance: "textfield" },
    // remove search decoration andremove webkit browser x on search input fields
    "&[type=search]::-webkit-search-decoration": {
      WebkitAppearance: "none"
    },
    "&[type=search]::-webkit-search-cancel-button": {
      WebkitAppearance: "none"
    },
    "&[type=search]::-webkit-search-results-button": {
      WebkitAppearance: "none"
    },
    "&[type=search]::-webkit-search-results-decoration": {
      WebkitAppearance: "none"
    }
  }
};
var ElementsPlainInput = class extends FormField {
  constructor() {
    super(...arguments);
    this.type = "text";
    this.focused = false;
  }
  styles() {
    return [inputResetStyle];
  }
  render() {
    if (this.type === "search")
      return html` <form action="." @submit="${(e) => e.preventDefault()}">${this.input()}</form> `;
    return this.input();
  }
  processNewValue(value, fromBlur) {
    value ?? (value = this._input.value);
    let adjustedValue = value;
    switch (this.type) {
      case "number":
        adjustedValue = this._handleNumberInput(value, fromBlur);
        break;
      case "iban":
        adjustedValue = this._handleIbanInput(value, fromBlur);
        break;
      case "tel":
        adjustedValue = this._handleTelInput(value, fromBlur);
        break;
      case "time":
        adjustedValue = this._handleTimeInput(value, fromBlur);
        break;
      case "date":
        adjustedValue = this._handleDateInput(value, fromBlur);
        break;
      case "datetime-local":
        adjustedValue = this._handleDateTimeLocalInput(value, fromBlur);
        break;
      case "week":
        adjustedValue = this._handleWeekInput(value, fromBlur);
        break;
      case "month":
        adjustedValue = this._handleMonthInput(value, fromBlur);
        break;
      default:
        adjustedValue = this._handleTextInput(value, fromBlur);
        break;
    }
    this.value.next(adjustedValue);
    return adjustedValue;
  }
  _applyLimit(adjustedValue, value) {
    value ?? (value = this._input.value);
    const limit = this.getAttribute("limit");
    if (limit && !isNaN(+limit)) {
      adjustedValue = value.length > +limit ? this.value.getValue() : this.type === "number" ? adjustedValue : value;
    }
  }
  _handleNumberInput(value, fromBlur) {
    value ?? (value = this._input.value);
    let adjustedValue = value;
    if (value === "")
      return 0;
    const dots = [46, 8228, 65106, 65294].map((charCode) => String.fromCharCode(charCode));
    let rawString = value.replace(",", ".");
    dots.forEach((dot) => rawString = rawString.replace(dot, "."));
    rawString = rawString.replace(/[^0-9.,-]/gi, "");
    if (rawString.indexOf(".") !== rawString.lastIndexOf("."))
      rawString = rawString.replace(".", "");
    if (rawString.charAt(rawString.length - 1) === ".")
      return value;
    const rawNumber = +rawString;
    const step = +(this.getAttribute("step") || 0);
    adjustedValue = !step ? rawNumber : Math.round(rawNumber / +step) * +step;
    const min = +(this.getAttribute("min") || -Infinity);
    const max = +(this.getAttribute("max") || Infinity);
    if (adjustedValue > max) {
      this.dispatchEvent(new CustomEvent("hit-max"));
      adjustedValue = max;
    }
    if (adjustedValue < min) {
      this.dispatchEvent(new CustomEvent("hit-min"));
      adjustedValue = min;
    }
    this._applyLimit(adjustedValue, value);
    const pad = this._getNumericAttributeSafe("pad");
    if (pad && !fromBlur)
      requestAnimationFrame(() => this.setCursor(pad));
    return adjustedValue;
  }
  _handleMonthInput(value, fromBlur) {
    value ?? (value = this._input.value);
    let adjustedValue = value;
    if (!value)
      return null;
    adjustedValue = DateTime.fromISO(value);
    return adjustedValue;
  }
  _handleWeekInput(value, fromBlur) {
    value ?? (value = this._input.value);
    let adjustedValue = value;
    if (!value)
      return null;
    adjustedValue = DateTime.fromISO(value);
    return adjustedValue;
  }
  _handleDateTimeLocalInput(value, fromBlur) {
    value ?? (value = this._input.value);
    let adjustedValue = value;
    if (!value)
      return null;
    adjustedValue = DateTime.fromISO(value);
    return adjustedValue;
  }
  _handleDateInput(value, fromBlur) {
    value ?? (value = this._input.value);
    let adjustedValue = value;
    if (!value)
      return null;
    adjustedValue = DateTime.fromISO(value);
    return adjustedValue;
  }
  _handleTimeInput(value, fromBlur) {
    value ?? (value = this._input.value);
    let adjustedValue = value;
    if (!value)
      return null;
    const step = this._getNumericAttributeSafe("step");
    adjustedValue = DateTime.fromFormat(value, step && step < 60 ? "TT" : "T");
    return adjustedValue;
  }
  _handleTelInput(value, fromBlur) {
    value ?? (value = this._input.value);
    let adjustedValue = value;
    if (!value.length || /^[+]*[(]{0,1}[0-9]{0,4}[)]{0,1}[-\s\.\/0-9]*$/.test(value))
      adjustedValue = value;
    else
      adjustedValue = this.value.getValue();
    return adjustedValue;
  }
  _handleIbanInput(value, fromBlur) {
    value ?? (value = this._input.value);
    let adjustedValue = value;
    const oldValue = this.value.getValue();
    const lastCharacter = value.slice(-1);
    if (!value.length)
      adjustedValue = value;
    else if (value.length <= 2)
      if (/[A-Za-z]/.test(lastCharacter))
        adjustedValue = value.toUpperCase();
      else
        adjustedValue = oldValue;
    else if (value.length <= 27) {
      if (/[0-9 ]/.test(lastCharacter))
        if ((value.length - 5) % 5 === 0 && lastCharacter === " ")
          adjustedValue = value;
        else if ((value.length - 5) % 5 === 0)
          adjustedValue = oldValue + " " + lastCharacter;
        else
          adjustedValue = value;
      else
        adjustedValue = oldValue;
    } else
      adjustedValue = oldValue;
    return adjustedValue;
  }
  _handleTextInput(value, fromBlur) {
    value ?? (value = this._input.value);
    let adjustedValue = value;
    this._applyLimit(adjustedValue, value);
    return adjustedValue;
  }
  connectedCallback() {
    super.connectedCallback();
    fromEvent(this, "click").pipe(takeUntil(this.disconnected)).subscribe(() => this._input?.focus());
  }
  input() {
    return html`
      <input
        @focus=${() => this.focused = true}
        @input=${(e) => {
      this.processNewValue();
    }}
        @search="${(e) => {
      this.processNewValue();
    }}"
        @keydown=${(e) => {
      if (this.type === "number") {
        const step = this._getNumericAttributeSafe("step") ?? 1;
        if (e.key.length === 1 && !/[0-9.,]/.test(e.key))
          e.preventDefault();
        if (e.key === "ArrowUp") {
          e.preventDefault();
          this.processNewValue(+(this.value.value ?? 0) + step + "");
        }
        if (e.key === "ArrowDown") {
          e.preventDefault();
          this.processNewValue(+(this.value.value ?? 0) - step + "");
        }
      }
    }}
        @keyup="${(e) => {
      if (e.key === "Enter")
        return this.dispatchEvent(
          new CustomEvent("submit", {
            detail: { value: this.processNewValue() },
            composed: true,
            bubbles: true
          })
        );
      if (e.key === "Escape")
        return this.dispatchEvent(new CustomEvent("escape"));
    }}"
        @blur=${(e) => {
      this.focused = false;
      this.processNewValue(void 0, true);
      this.dispatchEvent(new CustomEvent("value-picked", { detail: { value: this.value.getValue() } }));
    }}
        @click=${(e) => e.stopPropagation()}
        .value="${live(this._displayValue())}"
        ?disabled=${this.disabled}
        type=${this.type === "number" ? "text" : this.type}
        inputmode=${ifDefined(this.type === "number" ? "decimal" : void 0)}
        ${injectAttributesOf(this, ["type"])}
        ${ref((input) => this._input = input)}
      />
    `;
  }
  select() {
    if (isIos())
      this._input?.setSelectionRange(0, 999);
    else
      this._input?.select();
  }
  setCursor(position) {
    this._input?.setSelectionRange(position, position);
  }
  getCursor() {
    return this._input ? this._input.selectionStart : null;
  }
  focus() {
    this._input?.focus();
  }
  _getNumericAttributeSafe(attr) {
    const raw = this.getAttribute(attr);
    return raw && !isNaN(+raw) ? +raw : null;
  }
  _displayValue() {
    const value = this.value.getValue();
    if (value === void 0 || value === null || value === "")
      return "";
    const pad = this._getNumericAttributeSafe("pad");
    const step = this._getNumericAttributeSafe("step");
    if (this.type === "number" && pad)
      return String(value).padStart(pad, "0");
    if (this.type === "time")
      return value.toFormat(step && step < 60 ? "TT" : "T");
    if (this.type === "date")
      return value.toISODate();
    if (this.type === "datetime-local")
      return value.toISO({ suppressMilliseconds: true, suppressSeconds: true, includeOffset: false });
    if (this.type === "week")
      return value.toFormat("yyyy-'W'WW");
    if (this.type === "month")
      return value.toFormat("yyyy-MM");
    return value;
  }
};
__decorateClass([
  property({ reflect: true })
], ElementsPlainInput.prototype, "type", 2);
__decorateClass([
  state()
], ElementsPlainInput.prototype, "focused", 2);
ElementsPlainInput = __decorateClass([
  customElement("x-plain-input")
], ElementsPlainInput);
export {
  ElementsPlainInput,
  inputResetStyle
};
